import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

import {
    PrivaFilterSearchComponent,
    PrivaFilterSearchConfig,
    PrivaQueryableDataSource,
    PrivaQueryableDataSourceFactory,
    PrivaQueryUtils,
    PrivaSortDirection,
} from '@priva/components/filter';
import { PrivaSelectionGroupComponent, PrivaSelectionGroupItem } from '@priva/components/selection-group';

import { Activity } from '../../models';
import { ActivityFilterStore } from './activity-filter.store';

const sortByNumberSelectionItem = {
    label: '1-99',
    id: 'sort-by-number',
    icon: '',
};

const sortByNameSelectionItem = {
    label: 'A-Z',
    id: 'sort-by-name',
    icon: '',
};

@Component({
    selector: 'app-activity-filter',
    templateUrl: './activity-filter.component.html',
    styleUrl: './activity-filter.component.scss',
    imports: [AsyncPipe, PrivaFilterSearchComponent, PrivaSelectionGroupComponent],
    providers: [ActivityFilterStore],
})
export class ActivityFilterComponent {
    private destroyRef = inject(DestroyRef);
    dataSourceFactory = inject(PrivaQueryableDataSourceFactory);
    private queryUtils = inject(PrivaQueryUtils);
    private componentStore = inject(ActivityFilterStore);

    activities = input(undefined, {
        transform: (value: Activity[]) => {
            this.componentStore.updateActivities(value);
        },
    });
    resetSearchTermsSignal = input(undefined, {
        transform: () => {
            this.activitiesDataSource.addSearch(
                this.queryUtils.mapSearchInputForQuery(this.filterSearchConfig, ''),
            );
        },
    });

    readonly activitiesDataSource: PrivaQueryableDataSource<Activity>;
    readonly filteredActivitiesChanged = output<Activity[]>();

    filterSearchConfig: PrivaFilterSearchConfig = {
        type: 'search',
        id: 'searchActivities',
        title: 'Search',
        placeholder: 'GENERAL.TITLE.SEARCH_ACTIVITY',
        options: ['name', 'number'],
        searchOptions: {
            matchType: 'single',
        },
    };

    sortingGroupItems: PrivaSelectionGroupItem[] = [sortByNumberSelectionItem, sortByNameSelectionItem];

    selectedSortItem$ = this.componentStore.sortMode$.pipe(
        map((sortMode) => {
            switch (sortMode) {
                case 'name':
                    return sortByNameSelectionItem;
                case 'number':
                default:
                    return sortByNumberSelectionItem;
            }
        }),
    );

    constructor() {
        this.activitiesDataSource = this.dataSourceFactory.create();
        this.activitiesDataSource.setData(this.componentStore.activities$);

        this.activitiesDataSource.results$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((filteredActivities: Activity[]) =>
                this.filteredActivitiesChanged.emit(filteredActivities),
            );

        this.componentStore.sortMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((sortMode) => {
            switch (sortMode) {
                case 'name':
                    this.activitiesDataSource.addSort({ name: PrivaSortDirection.ASCENDING });
                    break;
                case 'number':
                    this.activitiesDataSource.addSort({ number: PrivaSortDirection.ASCENDING });
                    break;
                default:
            }
        });
    }

    sortItemSelected(selectedItem: PrivaSelectionGroupItem) {
        if (selectedItem.id === sortByNumberSelectionItem.id) {
            this.componentStore.sortModeUpdating('number');
        } else if (selectedItem.id === sortByNameSelectionItem.id) {
            this.componentStore.sortModeUpdating('name');
        }
    }
}
